import { Link } from "@tanstack/react-router";
import { ArrowRight } from "lucide-react";

/**
 * "Answer tease" block for Google AI Overviews / AI snippets.
 *
 * Zero-click loss happens when a page hands the generative answer box a
 * self-contained answer: the model quotes it, the searcher never leaves
 * google.com and the citation earns nothing. The industry pattern that keeps
 * the citation *and* the click is an open loop:
 *
 *  1. `answer` — a complete, quotable sentence set that answers the *question*
 *     definitionally (what it is, how it is measured, how often it changes).
 *     This is what gets cited, so it must be accurate and self-standing.
 *  2. `withheld` — the specific, volatile payload the answer deliberately does
 *     NOT contain (today's figure, the per-card rows, the live chart). Named
 *     concretely so the searcher knows the number exists but only resolves on
 *     the page. A model cannot substitute for data it was never given.
 *  3. `cta` — one in-page or in-site destination for that payload.
 *
 * Rendered as visible content (never hidden text) and marked with
 * `.aeo-answer` so it lines up with the site's existing AEO markers, plus
 * Speakable so assistants read the tease rather than the whole page.
 */
export interface SnippetFunnelProps {
  /** Question this block answers, phrased the way people search it. */
  question: string;
  /** Quotable answer: definition + method + refresh cadence. No live figures. */
  answer: string;
  /** Concrete data points that only exist on the page. */
  withheld: string[];
  /** Where the withheld data lives. */
  cta: { to: string; label: string };
  className?: string;
}

export function SnippetFunnel({ question, answer, withheld, cta, className = "" }: SnippetFunnelProps) {
  return (
    <section
      className={`glass rounded-2xl p-5 ${className}`}
      aria-labelledby="snippet-answer-heading"
      data-aeo="snippet-funnel"
    >
      <h2 id="snippet-answer-heading" className="snippet-question text-lg font-bold">
        {question}
      </h2>
      <p className="aeo-answer snippet-answer mt-2 max-w-3xl text-sm leading-relaxed text-muted-foreground">{answer}</p>
      <p className="mt-4 text-xs font-semibold uppercase tracking-widest text-primary">Only on this page</p>
      <ul className="mt-2 grid gap-1.5 text-sm text-muted-foreground sm:grid-cols-2">
        {withheld.map((w) => (
          <li key={w} className="flex gap-2">
            <span aria-hidden="true" className="text-primary">
              ·
            </span>
            <span>{w}</span>
          </li>
        ))}
      </ul>
      <Link
        to={cta.to}
        className="mt-4 inline-flex items-center gap-1.5 rounded-xl border border-primary/60 bg-primary/10 px-4 py-2 text-sm font-semibold text-foreground hover:border-primary"
      >
        {cta.label}
        <ArrowRight className="h-4 w-4" aria-hidden="true" />
      </Link>
    </section>
  );
}
